import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { submitContactForm } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { 
  Mail, Phone, MapPin, Clock, Send, 
  HeartHandshake, Landmark, ShieldAlert 
} from 'lucide-react';
import SeoHead from '@/components/Seo';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      toast({ title: "Message Sent!", description: "Thank you for contacting us. We'll get back to you soon." });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast({ title: "Message Received", description: "Your message has been logged. We'll contact you soon." });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fcfaf7]">
      <SeoHead
        title="Contact Us | Ibadan South Diocese"
        description="Reach out to the Diocese of Ibadan South (Anglican Communion)."
        ogImage="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/home_ss1tdy.jpg"
      />

      {/* Hero Header */}
      <div className="bg-slate-900 text-white pt-20 pb-32 px-4 relative overflow-hidden text-center">
        <div className="absolute inset-0 opacity-10">
          <MapPin className="absolute -left-10 -top-10 h-64 w-64 rotate-12" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">Get in Touch</h1>
          <p className="text-slate-400 text-lg md:text-xl font-serif italic">
            "Ask, and it will be given to you; seek, and you will find..." — Matthew 7:7
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-16 pb-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* LEFT: Contact Information & Support */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Primary Info */}
            <Card className="border-none shadow-xl rounded-[2rem] bg-white overflow-hidden">
              <CardContent className="p-8">
                <h2 className="text-2xl font-serif font-bold text-slate-900 mb-8 flex items-center gap-3">
                  <span className="h-8 w-1 bg-amber-500 rounded-full inline-block"></span>
                  Diocesan Secretariat
                </h2>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="bg-amber-50 p-3 rounded-2xl h-fit text-amber-600">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">Location</h4>
                      <p className="text-slate-600 leading-relaxed">
                        Cathedral Church of St. David, Kudeti<br />
                        Ibadan, Oyo State, Nigeria
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="bg-amber-50 p-3 rounded-2xl h-fit text-amber-600">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">Email Support</h4>
                      <p className="text-slate-600">info@ibadansouthanglicandiocese.org.ng</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="bg-amber-50 p-3 rounded-2xl h-fit text-amber-600">
                      <Clock className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">Office Hours</h4>
                      <p className="text-slate-600">Mon–Fri: 8:00am – 5:00pm</p>
                      <p className="text-slate-400 text-xs mt-1">Closed on Weekends for Services</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 p-6 bg-red-50 rounded-3xl border border-red-100 flex items-start gap-4">
                  <ShieldAlert className="h-6 w-6 text-red-500 shrink-0" />
                  <div>
                    <h4 className="font-bold text-red-900 text-sm uppercase">Emergency Pastoral Care</h4>
                    <p className="text-red-700 text-sm mt-1">Need urgent prayers or hospital visits? Call the Duty Chaplain:</p>
                    <p className="text-red-900 font-bold mt-1">+234 (0) XXX XXX XXXX</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Support Our Mission Card */}
            <Card className="border-none shadow-xl rounded-[2rem] bg-slate-900 text-white overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                   <div className="bg-amber-500/20 p-2 rounded-lg text-amber-500">
                    <HeartHandshake className="h-6 w-6" />
                   </div>
                   <h3 className="text-xl font-serif font-bold">Support Our Mission</h3>
                </div>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                  Your generosity supports our community outreach, church planting, and humanitarian efforts.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white/5 p-4 rounded-2xl border border-white/10 group hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <Landmark className="h-4 w-4 text-amber-500" />
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-300">First Bank (Current)</span>
                    </div>
                    <p className="text-xl font-mono font-bold text-amber-500 tracking-wider">2001017784</p>
                    <p className="text-[10px] text-slate-500 mt-1 uppercase">Ibadan South Diocese</p>
                  </div>

                  <div className="bg-white/5 p-4 rounded-2xl border border-white/10 group hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <Landmark className="h-4 w-4 text-amber-500" />
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-300">First Bank (Savings)</span>
                    </div>
                    <p className="text-xl font-mono font-bold text-amber-500 tracking-wider">3017518067</p>
                    <p className="text-[10px] text-slate-500 mt-1 uppercase">Ibadan South Diocese</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Contact Form */}
          <div className="lg:col-span-7">
            <Card className="border-none shadow-2xl rounded-[2rem] bg-white h-full">
              <CardContent className="p-8 md:p-12">
                <div className="mb-10">
                  <h2 className="text-3xl font-serif font-bold text-slate-900 mb-2">Send a Message</h2>
                  <p className="text-slate-500">We respond to all inquiries within 24 business hours.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400 ml-1">Full Name</label>
                      <Input 
                        name="name" 
                        placeholder="John Doe" 
                        className="rounded-xl border-slate-100 bg-slate-50 focus:bg-white h-12"
                        required 
                        value={formData.name} 
                        onChange={handleChange} 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400 ml-1">Email Address</label>
                      <Input 
                        name="email" 
                        type="email" 
                        placeholder="john@example.com" 
                        className="rounded-xl border-slate-100 bg-slate-50 focus:bg-white h-12"
                        required 
                        value={formData.email} 
                        onChange={handleChange} 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400 ml-1">Phone Number</label>
                      <Input 
                        name="phone" 
                        type="tel" 
                        placeholder="+234..." 
                        className="rounded-xl border-slate-100 bg-slate-50 focus:bg-white h-12"
                        value={formData.phone} 
                        onChange={handleChange} 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400 ml-1">Subject</label>
                      <Input 
                        name="subject" 
                        placeholder="Inquiry about..." 
                        className="rounded-xl border-slate-100 bg-slate-50 focus:bg-white h-12"
                        required 
                        value={formData.subject} 
                        onChange={handleChange} 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-400 ml-1">Your Message</label>
                    <Textarea 
                      name="message" 
                      rows={6} 
                      placeholder="How can we help you today?" 
                      className="rounded-2xl border-slate-100 bg-slate-50 focus:bg-white resize-none"
                      required 
                      value={formData.message} 
                      onChange={handleChange} 
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="w-full h-14 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl text-lg font-bold transition-all shadow-lg shadow-amber-600/20"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">Processing...</span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Message <Send className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;